<template>
  <Transition
    appear
    enter-active-class="transition transform duration-400 ease-in-out"
    enter-from-class="translate-y-full md:translate-y-0 md:translate-x-full opacity-0"
    leave-active-class="transition transform duration-200 ease-in-out"
    leave-to-class="translate-y-full md:translate-y-0 md:translate-x-full opacity-0"
    @after-leave="onTransitionComplete"
  >
    <div
      v-if="isVisible"
      :aria-describedby="aria.descriptionId"
      :aria-labelledby="aria.titleId"
      aria-modal="true"
      :class="wrapperClasses"
      role="dialog"
    >
      <div class="relative">
        <RevButtonIcon
          :aria-label="i18n(translations.closeLabel)"
          :class="closeButtonClasses"
          :icon="isNewBanner ? IconCrossSmall : IconCross"
          size="small"
          :variant="isNewBanner ? 'ghost' : 'primary'"
          @click="handleClose"
        />

        <!-- Hidden elements for screen readers -->
        <span :id="aria.titleId" class="sr-only">{{
          i18n(translations.ariaTitle)
        }}</span>
        <span :id="aria.descriptionId" class="sr-only">{{
          i18n(translations.ariaDescription)
        }}</span>

        <slot></slot>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts" setup>
import { useRoute } from '#imports'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useDataConsent } from '@backmarket/nuxt-module-tracking/useDataConsent'
import { tw } from '@backmarket/utils/string/tw'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { IconCross } from '@ds/icons/IconCross'
import { IconCrossSmall } from '@ds/icons/IconCrossSmall'

import translations from './BasePopup.translations'

const i18n = useI18n()
const route = useRoute()
const { isAppsFlyerUserConsentGiven } = useDataConsent()

const props = withDefaults(
  defineProps<{
    displayPopup: boolean
    bannerType?: 'old' | 'new'
  }>(),
  {
    displayPopup: false,
    bannerType: 'old',
  },
)

const emit = defineEmits(['closed'])

const aria = {
  titleId: 'lead-collection-aria-title-id',
  descriptionId: 'lead-collection-aria-description',
}

const isVisible = ref(false)

function show() {
  isVisible.value = true
}

function hide() {
  isVisible.value = false
}

function handleClose() {
  hide()
}

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isVisible.value) {
    handleClose()
  }
}

function onTransitionComplete() {
  if (!isVisible.value) {
    emit('closed')
  }
}

const isNewBanner = computed(() => props.bannerType === 'new')

const closeButtonClasses = computed(() => {
  return [
    tw`absolute z-[1] bg-transparent`,
    isNewBanner.value &&
      tw`!text-static-default-low right-2 -translate-y-1/2 md:!text-static-default-hi md:right-4 md:top-24`,
    !isNewBanner.value && tw`right-12 top-12`,
    isNewBanner.value && isAppsFlyerUserConsentGiven.value && tw`top-1/2`,
    isNewBanner.value && !isAppsFlyerUserConsentGiven.value && tw`top-24`,
  ]
})

const wrapperClasses = computed(() => {
  return [
    tw`shadow-long fixed z-10 md:bottom-60 md:right-24`,
    isNewBanner.value &&
      tw`rounded-lg bottom-12 max-md:left-[calc(50%-24px)] max-md:mx-24 max-md:w-max max-md:max-w-[calc(100vw-48px)] max-md:-translate-x-1/2 md:left-auto`,
    !isNewBanner.value && tw`bottom-0`,
  ]
})

watch(
  () => props.displayPopup,
  (displayPopup) => {
    const action = displayPopup ? show : hide
    action()
  },
  { immediate: true },
)

watch(
  () => route.fullPath,
  () => {
    handleClose()
  },
)

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyDown)
})
</script>
